import "./../areaChart.css";

import * as d3 from "d3";
import React, { useEffect } from "react";

interface DemandVersusTimeLinePoints {
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    data: { periodName: string; periodNumber: number; demandCount: number }[];
    circleRadius?: number;
    mobile: boolean;
    d3Id: string;
}

export const DemandVersusTimeLinePoints = (
    props: DemandVersusTimeLinePoints
) => {
    const { xScale, yScale, data, circleRadius = 2, mobile, d3Id } = props;

    const getBubbleWidth = (demandCount: number) => {
        const baseWidth = mobile ? 160 : 38;
        const scaleFactor = mobile ? 8 : 4;
        const digitCount = demandCount === 0 ? 1 : Math.log10(demandCount) + 1;

        return baseWidth + Math.floor(digitCount) * scaleFactor;
    };
    // these handle conditions where the tooltip might appear off screen for high or low percentiles
    const getBubbleXPosition = (d: {
        periodName: string;
        periodNumber: number;
        demandCount: number;
    }) => {
        if (d.periodNumber >= 10 && mobile) return xScale(12);
        return xScale(d.periodNumber) - (mobile ? 64 : 16);
    };

    // these handle conditions where the tooltip might appear off screen for high or low percentiles
    const getSalaryXPosition = (d: {
        periodName: string;
        periodNumber: number;
        demandCount: number;
    }) => {
        if (d.periodNumber >= 10 && mobile) return xScale(12);
        return xScale(d.periodNumber) - (mobile ? 50 : 13);
    };

    useEffect(() => {
        const clickState = data.reduce((acc, { periodNumber }) => {
            acc[periodNumber] = "hidden";
            return acc;
        }, {});

        const bubbleContainer = d3.select(`#${d3Id}`).selectAll(".line-points");

        bubbleContainer
            .append("g")
            .selectAll("val")
            .data(data)
            .enter()
            .append("rect")
            .attr("class", d => `tooltip${d.periodNumber}`)
            .attr("width", d => getBubbleWidth(d.demandCount))
            .attr("height", mobile ? 48 : 12)
            .attr("rx", 2)
            .attr("ry", 2)
            .style("fill", "white")
            .attr("stroke", "#54d2c7")
            .attr("stroke-width", mobile ? 2 : 0.5)
            .style("visibility", "hidden")
            .attr("y", d => yScale(d.demandCount) - (mobile ? 64 : 16))
            .attr("x", d => getBubbleXPosition(d));

        bubbleContainer
            .append("g")
            .selectAll("val")
            .data(data)
            .enter()
            .append("text")
            .text(
                d =>
                    `${d3.format(",")(d.demandCount)} job posting${
                        d.demandCount !== 1 ? "s" : ""
                    }`
            )
            .attr("class", d => `tooltip${d.periodNumber}`)
            .style("visibility", "hidden")
            .style("fill", "#54d2c7")
            .attr("font-size", mobile ? 24 : 6)
            .attr("font-weight", 200)
            .attr("y", d => yScale(d.demandCount) - (mobile ? 30 : 7.5))
            .attr("x", d => getSalaryXPosition(d));

        /* Add circles in the line */
        d3.select(`#${d3Id}`)
            .selectAll(".line-points")
            .selectAll("circle-group")
            .data([data])
            .enter()
            .append("g")
            .style("fill", "white")
            .selectAll("circle")
            .data((d, i) => d)
            .enter()
            .append("g")
            .append("circle")
            .attr("class", (d, i) => `circle${d.periodNumber}`)
            .attr("cx", d => xScale(d.periodNumber))
            .attr("cy", d => yScale(d.demandCount))
            .attr("r", mobile ? 8 : 2)
            .style("stroke", "#54d2c7")
            .attr("stroke-width", mobile ? 2 : 0.5)
            .on(
                "mouseleave",
                mobile
                    ? null
                    : d => {
                          d3.selectAll(`.circle${d.periodNumber}`)
                              .style("fill", "white")
                              .attr("r", circleRadius);
                          d3.selectAll(`.tooltip${d.periodNumber}`).style(
                              "visibility",
                              "hidden"
                          );
                      }
            )
            .on(
                "mouseover",
                mobile
                    ? null
                    : d => {
                          d3.selectAll(`.circle${d.periodNumber}`)
                              .style("fill", "#54d2c7")
                              .attr("r", circleRadius + 0.5);
                          d3.selectAll(`.tooltip${d.periodNumber}`).style(
                              "visibility",
                              "visible"
                          );
                      }
            )
            .on("click", d => {
                const periodNumberKey = d.periodNumber;
                d3.selectAll(`.tooltip${d.periodNumber}`).style(
                    "visibility",
                    clickState[periodNumberKey] === "hidden"
                        ? "visible"
                        : "hidden"
                );
                if (clickState[periodNumberKey] === "hidden") {
                    clickState[periodNumberKey] = "visible";
                    d3.selectAll(`.circle${d.periodNumber}`).style(
                        "fill",
                        "#54d2c7"
                    );
                } else {
                    clickState[periodNumberKey] = "hidden";
                    d3.selectAll(`.circle${d.periodNumber}`).style(
                        "fill",
                        "white"
                    );
                }
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <g className="line-points"></g>;
};
